import {
	Card,
	Table,
	Button,
	Tag,
	Space,
	Input,
	Select,
	Row,
	Col,
	Typography,
	message,
	notification,
	Popconfirm,
	Tooltip,
	Avatar,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
	PiPlusDuotone,
	PiPencilDuotone,
	PiTrashDuotone,
	PiEyeDuotone,
	PiUsersDuotone,
	PiCrownDuotone,
	PiHouseDuotone,
} from "react-icons/pi";
import AppContext from "../../store/app.context";
import { requestGetTeamList, requestDeleteTeam } from "../../services/admin.service";
import SearchInput from "../../components/SearchInput";

const { Title, Text } = Typography;
const { Option } = Select;

function TeamsListPage() {
	const context = useContext(AppContext);
	const navigate = useNavigate();
	const [loading, setLoading] = useState(false);
	const [teams, setTeams] = useState([]);
	const [pagination, setPagination] = useState({
		current: 1,
		pageSize: 10,
		total: 0,
	});
	const [filters, setFilters] = useState({
		keyword: "",
		currency: "",
	});

	useEffect(() => {
		document.title = "Cloudiva Console | Teams";
		fetchTeams();
	}, []);

	useEffect(() => {
		fetchTeams();
	}, [pagination.current, pagination.pageSize, filters]);

	const fetchTeams = () => {
		setLoading(true);
		const params = {
			page: pagination.current,
			limit: pagination.pageSize,
			keyword: filters.keyword,
			order: "name:asc",
		};

		// Build filter string
		const filterParts = [];
		if (filters.currency) {
			filterParts.push(`currency:${filters.currency}`);
		}
		if (filterParts.length > 0) {
			params.filter = filterParts.join(",");
		}

		requestGetTeamList(params)
			.then((response) => {
				// Server response structure: { code, message, pagination, data }
				const teamsData = response.data.data || [];
				const serverPagination = response.data.pagination || {};

				setTeams(teamsData);
				setPagination((prev) => ({
					...prev,
					total: serverPagination.total || 0,
				}));
			})
			.catch((error) => {
				console.error("Error fetching teams:", error);
				notification.error({
					message: "Error",
					description: "Failed to fetch teams",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleDeleteTeam = (id) => {
		requestDeleteTeam(id)
			.then(() => {
				message.success("Team deleted successfully");
				fetchTeams();
			})
			.catch((error) => {
				console.error("Error deleting team:", error);
				if (error.response?.data?.message) {
					notification.error({
						message: "Error",
						description: error.response.data.message,
					});
				} else {
					notification.error({
						message: "Error",
						description: "Failed to delete team. Please try again.",
					});
				}
			});
	};

	const handleTableChange = (newPagination) => {
		setPagination(newPagination);
	};

	const handleSearch = (keyword) => {
		setFilters((prev) => ({ ...prev, keyword }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const handleCurrencyFilter = (currency) => {
		setFilters((prev) => ({ ...prev, currency }));
		setPagination((prev) => ({ ...prev, current: 1 }));
	};

	const columns = [
		{
			title: "Team",
			dataIndex: "name",
			key: "name",
			render: (text, record) => (
				<div style={{ display: "flex", alignItems: "center" }}>
					<PiUsersDuotone size={24} style={{ marginRight: 12, color: "#1890ff" }} />
					<div>
						<div style={{ fontWeight: 500 }}>{text}</div>
						{record.description && (
							<div style={{ fontSize: "12px", color: "#666" }}>{record.description}</div>
						)}
					</div>
				</div>
			),
		},
		{
			title: "Owner",
			dataIndex: "owner",
			key: "owner",
			render: (owner) => (
				<div style={{ display: "flex", alignItems: "center" }}>
					<PiCrownDuotone style={{ marginRight: 4, color: "#faad14" }} />
					<div>
						<div style={{ fontWeight: 500 }}>{owner?.name}</div>
						<div style={{ fontSize: "12px", color: "#666" }}>{owner?.email}</div>
					</div>
				</div>
			),
		},
		{
			title: "Members",
			dataIndex: "members",
			key: "members",
			render: (members) => (
				<div style={{ display: "flex", alignItems: "center" }}>
					<Avatar.Group maxCount={3} size="small">
						{members?.map((member) => (
							<Avatar key={member._id} size="small">
								{member.name?.charAt(0)?.toUpperCase()}
							</Avatar>
						))}
					</Avatar.Group>
					<Text style={{ marginLeft: 8, fontSize: "12px", color: "#666" }}>
						{members?.length || 0} member{members?.length !== 1 ? "s" : ""}
					</Text>
				</div>
			),
		},
		{
			title: "Created At",
			dataIndex: "createdAt",
			key: "createdAt",
			render: (date) => new Date(date).toLocaleDateString(),
		},
		{
			title: "Action",
			key: "action",
			align: "right",
			render: (_, record) => (
				<Space size="middle">
					<Tooltip title="View Team">
						<Button
							type="text"
							size="small"
							icon={<PiEyeDuotone />}
							onClick={() => navigate(`/teams/${record._id}`)}
						/>
					</Tooltip>
					<Tooltip title="Edit Team">
						<Button
							type="text"
							size="small"
							icon={<PiPencilDuotone />}
							onClick={() => navigate(`/teams/${record._id}/edit`)}
						/>
					</Tooltip>
					<Popconfirm
						title="Delete Team"
						description="Are you sure you want to delete this team? This action cannot be undone."
						onConfirm={() => handleDeleteTeam(record._id)}
						okText="Yes"
						cancelText="No"
						okType="danger">
						<Tooltip title="Delete Team">
							<Button type="text" size="small" danger icon={<PiTrashDuotone />} />
						</Tooltip>
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space>
					<Button icon={<PiHouseDuotone />} onClick={() => navigate("/")}></Button>
					<Title level={4} style={{ margin: 0 }}>
						Team Management
					</Title>
				</Space>
			</div>
			<div className="page-content-wrapper">
				<Card
					title="All Teams"
					extra={
						<Button type="primary" icon={<PiPlusDuotone />} onClick={() => navigate("/teams/add")}>
							Add Team
						</Button>
					}>
					<Row gutter={16} style={{ marginBottom: 16 }}>
						<Col span={8}>
							<SearchInput
								placeholder="Search teams..."
								onSearch={handleSearch}
								style={{ width: "100%" }}
							/>
						</Col>
						<Col span={4}>
							<Select
								placeholder="Currency"
								onChange={handleCurrencyFilter}
								allowClear
								style={{ width: "100%" }}>
								<Option value="IDR">IDR</Option>
								<Option value="USD">USD</Option>
								<Option value="EUR">EUR</Option>
								<Option value="GBP">GBP</Option>
								<Option value="JPY">JPY</Option>
							</Select>
						</Col>
					</Row>
					<Table
						columns={columns}
						dataSource={teams}
						loading={loading}
						pagination={{
							...pagination,
							showSizeChanger: true,
							showQuickJumper: true,
							showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} teams`,
						}}
						onChange={handleTableChange}
						rowKey="_id"
					/>
				</Card>
			</div>
		</div>
	);
}

export default TeamsListPage;
